import * as React from 'react';
import createDropViewState from 'owa-dnd/lib/utils/createDropViewState';
import ensureDragTipMounted from '../content/ensureDragTipMounted';
import { DragData } from 'owa-dnd/lib/utils/dragDataUtil';
import { DraggableItemTypes } from 'owa-dnd/lib/utils/DraggableItemTypes';
import { dropCalendarItem } from '../../store/actions/dropCalendarItem';
import { DroppableCore } from 'owa-dnd/lib/components/Droppable';
import { endDragOperation } from '../../store/actions/endDragOperation';
import { handleStartDragOntoSurface } from '../../store/actions/handleStartDragOntoSurface';
import { pauseDragOperation } from '../../store/actions/pauseDragOperation';
import { performVerticalAutoScroll } from 'owa-react-hooks/lib/performVerticalAutoScroll';
import { startOrUpdateDragOperation } from '../../store/actions/startOrUpdateDragOperation';
import {
    CalendarSurfaceStore,
    ColumnProvider,
    DroppableType,
    SurfaceDragDropConfig,
} from 'owa-calendar-surface-dategrid';

/**
 * Props for the surface droppable component
 */
export interface SurfaceDroppableHooksProps {
    store: CalendarSurfaceStore;
    columnProvider?: ColumnProvider;
    dragDropConfig: SurfaceDragDropConfig;
    droppableType: DroppableType;
    style?: React.CSSProperties;
    calendarFolderId?: string;
}

export default function useSurfaceDroppableHooks(props: SurfaceDroppableHooksProps) {
    let { current: asyncId } = React.useRef<number>();
    let { current: droppableRect } = React.useRef<ClientRect | null>(null);
    let { current: shouldProcessDragEvent } = React.useRef<boolean>(true);

    const canDrop = (dragInfo: DragData) => {
        let { dragDropConfig } = props;
        return dragDropConfig.canDrop(dragInfo.itemType, props.calendarFolderId);
    };

    const onDragLeave = (dragInfo: DragData) => {
        let { store } = props;
        pauseDragOperation(store);
    };

    const startOrUpdateThisDragOperation = (
        pageX: number,
        pageY: number,
        droppableElement: HTMLElement
    ) => {
        let { store, columnProvider, dragDropConfig, droppableType } = props;

        startOrUpdateDragOperation(
            store,
            dragDropConfig,
            columnProvider,
            droppableElement,
            droppableType,
            pageX,
            pageY
        );
    };

    const onDragOver = (
        dragInfo: DragData,
        pageX: number,
        pageY: number,
        droppableElement: HTMLElement
    ) => {
        if (shouldProcessDragEvent) {
            shouldProcessDragEvent = false;
            ensureDraggableState(dragInfo, pageX, pageY, droppableElement);
            ensureDroppableRect(droppableElement);
            // This is needed to enable auto-scroll in IE11
            performVerticalAutoScroll(droppableElement, droppableRect, pageY);

            if (dragInfo.itemType !== DraggableItemTypes.LocalFile) {
                startOrUpdateThisDragOperation(pageX, pageY, droppableElement);
            }

            // prevent processing of drag events until the next re-paint (to avoid batching a bunch of drag events, which looks laggy)
            if (!asyncId) {
                asyncId = window.requestAnimationFrame(() => {
                    shouldProcessDragEvent = true;
                    asyncId = null;
                });
            }
        }
    };

    const onDrop = (
        dragInfo: DragData,
        pageX: number,
        pageY: number,
        droppableElement: HTMLElement
    ) => {
        let { store, dragDropConfig } = props;
        startOrUpdateThisDragOperation(pageX, pageY, droppableElement);
        dropCalendarItem(store, dragDropConfig, dragInfo);

        endDragOperation(store, dragDropConfig);
    };

    const ensureDroppableRect = droppableElement => {
        if (!droppableRect) {
            droppableRect = droppableElement.getBoundingClientRect();
        }
    };

    const ensureDraggableState = (
        dragInfo: DragData,
        pageX: number,
        pageY: number,
        droppableElement: HTMLElement
    ) => {
        let { store, columnProvider, dragDropConfig, droppableType } = props;

        if (!store.dragDropState.draggedItemModel) {
            handleStartDragOntoSurface(
                store,
                dragDropConfig,
                columnProvider,
                droppableElement,
                droppableType,
                pageX,
                pageY,
                dragInfo.itemType
            );
            ensureDragTipMounted(store);
        }
    };

    const droppableCore = React.useMemo(
        () =>
            new DroppableCore({
                dropViewState: createDropViewState(),
                canDrop: canDrop,
                onDrop: onDrop,
                onDragLeave: onDragLeave,
                onDragOver: onDragOver,
                greedy: true,
            }),
        [
            props.store,
            props.columnProvider,
            props.dragDropConfig,
            props.droppableType,
            props.calendarFolderId,
        ]
    );

    React.useEffect(() => {
        return () => window.cancelAnimationFrame(asyncId);
    }, []);

    const onDropEvent = React.useCallback(
        (ev: React.DragEvent<HTMLDivElement>) => {
            droppableCore.onDrop(ev);
        },
        [droppableCore]
    );

    const onDragOverEvent = React.useCallback(
        (ev: React.DragEvent<HTMLDivElement>) => {
            droppableCore.onDragOver(ev);
        },
        [droppableCore]
    );

    const onDragEnterEvent = React.useCallback(
        (ev: React.DragEvent<HTMLDivElement>) => {
            droppableCore.onDragEnter(ev);
        },
        [droppableCore]
    );

    const onDragLeaveEvent = React.useCallback(
        (ev: React.DragEvent<HTMLDivElement>) => {
            droppableCore.onDragLeave(ev);
        },
        [droppableCore]
    );

    return {
        children: null,
        handlers: {
            onDrop: onDropEvent,
            onDragOver: onDragOverEvent,
            onDragEnter: onDragEnterEvent,
            onDragLeave: onDragLeaveEvent,
        },
    };
}
